import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AdvancedFilter, AdvancedFilterSelectedOptions, Dropdown, Icon, Skeleton } from '@sendible/design-system';
import { useBridgeContext } from '@sendible/shared-state-bridge';
import { reportErrorToFaro } from '@sendible/common';
import {
  Container as ReportsListContainer,
  Content as ReportsListContent,
  Section,
  TemplateSection,
  SectionTitle,
  SectionHeader,
  TemplateGrid,
  TemplateCard,
  TemplateCardHeader,
  TemplateIcon,
  TemplateName,
  TemplateDescription,
  TemplateInfoContainer,
  ReportTable,
  ReportTableHead,
  ReportTableBody,
  ReportTableHeaderCell,
  ReportTableRow,
  ReportTableCell,
  ReportTableCellName,
  ReportTableCellNameButton,
  ReportTableCellProfiles,
  ProfileIconsContainer,
  ReportTableCellActions,
  ProfileOverflowCount,
  ReportCardOptionsButton,
  EmptyState,
  EmptyStateIcon,
  EmptyStateTitle,
  EmptyStateSubtitle,
} from './index.styles';
import { useGetReports, useGetProfiles, useDuplicateReport, useDeleteReport, useGetTemplates } from '../../../../models/reporting';
import { transformApiReportToListItem } from '../../../../models/reporting/transforms';
import { formatRelativeTime, PLATFORM_COLOR_ICON_MAP } from '../../helpers';
import { DeleteReportModal } from '..';

type ReportsUiState = { kind: 'loading' } | { kind: 'results'; items: ReportListItem[] } | { kind: 'empty'; isSearching: boolean };

const MAX_VISIBLE_PROFILES = 3;

const TEMPLATE_CARD_CONFIGS = [
  {
    platformType: 'TikTok' as PlatformType,
    nameKey: 'new_reports_tiktok_template_name',
    descriptionKey: 'new_reports_tiktok_template_description',
    icon: 'tiktok_color',
  },
  {
    platformType: 'Facebook' as PlatformType,
    nameKey: 'new_reports_facebook_template_name',
    descriptionKey: 'new_reports_facebook_template_description',
    icon: 'facebook_color',
  },
] as const;

export const ReportsList = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('reports');
  const [{ user }] = useBridgeContext();
  const [reportToDelete, setReportToDelete] = useState<{ id: string; name: string } | null>(null);
  const [selectedFilters, setSelectedFilters] = useState<AdvancedFilterSelectedOptions[]>([]);

  const workspaceId = user.userId;

  // Extract search query from the text filter if present
  const textFilter = selectedFilters.find((f) => f.id === 'text');
  const searchQuery = textFilter?.selected?.[0] || undefined;

  const { data: apiReports, isLoading: isLoadingReports, isFetching: isFetchingReports } = useGetReports(workspaceId, searchQuery);
  const { data: allProfiles } = useGetProfiles();
  const { data: apiTemplates } = useGetTemplates();

  const reports = useMemo(() => (apiReports ? apiReports.map(transformApiReportToListItem) : []), [apiReports]);

  // Derived UI state evaluated purely during render pass
  const uiState = useMemo<ReportsUiState>(() => {
    if (isFetchingReports || isLoadingReports) {
      return { kind: 'loading' };
    }
    if (reports.length > 0) {
      return { kind: 'results', items: reports };
    }

    return { kind: 'empty', isSearching: !!searchQuery };
  }, [isFetchingReports, isLoadingReports, reports, searchQuery]);

  // Mutations
  const duplicateReport = useDuplicateReport();

  // Build a lookup map from platformType → first matching API template ID
  const templateIdByPlatform = useMemo(() => {
    if (!apiTemplates) return new Map<string, string>();

    return apiTemplates.reduce((map: Map<string, string>, template: { platformType: string; id: string }) => {
      if (!map.has(template.platformType)) {
        map.set(template.platformType, template.id);
      }

      return map;
    }, new Map<string, string>());
  }, [apiTemplates]);

  // Build a lookup map from profile URN → ReportProfile for fast resolution
  const profileMap = useMemo(() => {
    if (!allProfiles) return new Map<string, ReportProfile>();

    return new Map(allProfiles.map((p: ReportProfile) => [p.id, p]));
  }, [allProfiles]);

  const handleCreateBlank = () => {
    navigate('/reporting/new');
  };

  const handleCreateFromTemplate = (templateId: string) => {
    navigate(`/reporting/new?template=${templateId}`);
  };

  const handleOpenReport = (reportId: string) => {
    navigate(`/reporting/${reportId}`);
  };

  const handleMenuAction = useCallback(
    async (action: string, reportId: string) => {
      switch (action) {
        case 'open':
          navigate(`/reporting/${reportId}`);
          break;
        case 'duplicate': {
          try {
            const result = await duplicateReport.mutateAsync({
              reportId,
            });

            if (result?.id) {
              navigate(`/reporting/${result.id}`);
            }
          } catch (error) {
            reportErrorToFaro(error, 'ReportsList handleDuplicate');
          }
          break;
        }
        case 'delete': {
          const found = reports.find((r: ReportListItem) => r.id === reportId);

          setReportToDelete(found ? { id: found.id, name: found.name } : null);
          break;
        }
        default:
          break;
      }
    },
    [navigate, reports, duplicateReport]
  );

  const deleteReport = useDeleteReport(reportToDelete?.id ?? '');

  const handleDeleteReport = useCallback(async () => {
    if (!reportToDelete?.id) return;

    try {
      await deleteReport.mutateAsync({});
    } catch (error) {
      reportErrorToFaro(error, 'ReportsList handleDeleteReport');
    } finally {
      setReportToDelete(null);
    }
  }, [reportToDelete?.id, deleteReport]);

  const getReportProfileIcons = (report: (typeof reports)[0]) => {
    const ids = report.profileIds ?? [];
    const resolved = ids.map((id: string) => profileMap.get(id)).filter(Boolean) as ReportProfile[];
    const visible = resolved.slice(0, MAX_VISIBLE_PROFILES);
    const overflow = resolved.length - MAX_VISIBLE_PROFILES;

    return { visible, overflow };
  };

  const renderReports = () => {
    if (uiState.kind === 'loading') {
      return (
        <ReportTable>
          <colgroup>
            <col style={{ width: '37.72%' }} />
            <col style={{ width: '19.75%' }} />
            <col style={{ width: '10.77%' }} />
            <col style={{ width: '24.6%' }} />
            <col style={{ width: '7.18%' }} />
          </colgroup>
          <ReportTableBody>
            {Array.from({ length: 4 }).map((_, i) => (
              <ReportTableRow
                key={`skel-report-${i}`}
                style={{ cursor: 'default' }}
              >
                <ReportTableCellName $paddingLeft="1rem">
                  <Skeleton
                    width={`${10 + (i % 3) * 4}rem`}
                    height="0.875rem"
                  />
                </ReportTableCellName>
                <ReportTableCell>
                  <Skeleton
                    width="5rem"
                    height="0.875rem"
                  />
                </ReportTableCell>
                <ReportTableCell>
                  <Skeleton
                    width="2rem"
                    height="0.875rem"
                  />
                </ReportTableCell>
                <ReportTableCell>
                  <Skeleton
                    width="4rem"
                    height="0.875rem"
                  />
                </ReportTableCell>
                <ReportTableCellActions>
                  <Skeleton
                    width="1.5rem"
                    height="1.5rem"
                  />
                </ReportTableCellActions>
              </ReportTableRow>
            ))}
          </ReportTableBody>
        </ReportTable>
      );
    }

    if (uiState.kind === 'empty') {
      return (
        <EmptyState>
          <EmptyStateIcon>
            <Icon
              color="brandDark"
              name={uiState.isSearching ? 'search' : 'danger'}
            />
          </EmptyStateIcon>
          <EmptyStateTitle>{uiState.isSearching ? t('new_reports_search_no_results') : t('new_reports_no_reports_found')}</EmptyStateTitle>
          {!uiState.isSearching && <EmptyStateSubtitle>{t('new_reports_no_reports_hint')}</EmptyStateSubtitle>}
        </EmptyState>
      );
    }

    return (
      <ReportTable>
        <colgroup>
          <col style={{ width: '37.72%' }} />
          <col style={{ width: '19.75%' }} />
          <col style={{ width: '10.77%' }} />
          <col style={{ width: '24.6%' }} />
          <col style={{ width: '7.18%' }} />
        </colgroup>
        <ReportTableHead>
          <ReportTableRow>
            <ReportTableHeaderCell
              $paddingLeft="1rem"
              scope="col"
            >
              {t('new_reports_table_header_name')}
            </ReportTableHeaderCell>
            <ReportTableHeaderCell scope="col">{t('new_reports_table_header_date_modified')}</ReportTableHeaderCell>
            <ReportTableHeaderCell scope="col">{t('new_reports_table_header_modules')}</ReportTableHeaderCell>
            <ReportTableHeaderCell
              scope="col"
              aria-label={t('new_reports_table_header_profiles') ?? ''}
            />
            <ReportTableHeaderCell
              scope="col"
              aria-label={t('new_reports_table_header_actions') ?? ''}
            />
          </ReportTableRow>
        </ReportTableHead>
        <ReportTableBody>
          {uiState.items.map((report) => {
            const { visible: visibleProfiles, overflow } = getReportProfileIcons(report);

            return (
              <ReportTableRow
                key={report.id}
                data-testid="report-row"
                onClick={(e) => {
                  if (!(e.target as HTMLElement).closest('[data-stop-row-click]')) {
                    handleOpenReport(report.id);
                  }
                }}
              >
                <ReportTableCellName $paddingLeft="1rem">
                  <ReportTableCellNameButton
                    tabIndex={0}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenReport(report.id);
                    }}
                  >
                    {report.name}
                  </ReportTableCellNameButton>
                </ReportTableCellName>
                <ReportTableCell>{report.updatedAt ? formatRelativeTime(report.updatedAt) : ''}</ReportTableCell>
                <ReportTableCell>{report.moduleCount ?? 0}</ReportTableCell>
                <ReportTableCellProfiles>
                  <ProfileIconsContainer>
                    {visibleProfiles.map((profile) => {
                      const colorIcon = PLATFORM_COLOR_ICON_MAP[profile.platform];

                      return colorIcon ? (
                        <Icon
                          key={profile.id}
                          name={colorIcon}
                        />
                      ) : null;
                    })}
                    {overflow > 0 && <ProfileOverflowCount>+{overflow}</ProfileOverflowCount>}
                  </ProfileIconsContainer>
                </ReportTableCellProfiles>
                <ReportTableCellActions>
                  <span
                    data-stop-row-click=""
                    role="none"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Dropdown
                      options={[
                        { action: 'open', label: t('new_reports_menu_open') ?? '', icon: 'arrow_up_right', iconColor: 'brandDarkTint20' },
                        { action: 'duplicate', label: t('new_reports_menu_duplicate') ?? '', icon: 'medias', iconColor: 'brandDarkTint20' },
                        { type: 'divider' },
                        { action: 'delete', label: t('new_reports_menu_delete') ?? '', icon: 'trash', iconColor: 'brandDarkTint20' },
                      ]}
                      select={(action) => handleMenuAction(action, report.id)}
                      placement="bottom-start"
                      width="11rem"
                    >
                      <ReportCardOptionsButton
                        type="button"
                        aria-label={t('new_reports_report_options') ?? ''}
                        data-testid="report-options-btn"
                      >
                        <Icon
                          color="brandDarkTint20"
                          name="more_vertical"
                        />
                      </ReportCardOptionsButton>
                    </Dropdown>
                  </span>
                </ReportTableCellActions>
              </ReportTableRow>
            );
          })}
        </ReportTableBody>
      </ReportTable>
    );
  };

  return (
    <ReportsListContainer>
      <ReportsListContent>
        {/* ── Templates section ── */}
        <TemplateSection>
          <SectionTitle>{t('new_reports_start_new_report')}</SectionTitle>
          <TemplateGrid>
            {/* Blank report card */}
            <TemplateCard
              $isBlank
              type="button"
              onClick={handleCreateBlank}
            >
              <TemplateCardHeader>
                <TemplateIcon>
                  <Icon
                    color="brandDark"
                    name="plus"
                  />
                </TemplateIcon>
              </TemplateCardHeader>
              <TemplateInfoContainer>
                <TemplateName>{t('new_reports_blank_report')}</TemplateName>
                <TemplateDescription>{t('new_reports_blank_report_description')}</TemplateDescription>
              </TemplateInfoContainer>
            </TemplateCard>

            {TEMPLATE_CARD_CONFIGS.map((config) => {
              const templateId = templateIdByPlatform.get(config.platformType);

              if (!templateId) return null;

              return (
                <TemplateCard
                  key={config.platformType}
                  type="button"
                  onClick={() => handleCreateFromTemplate(templateId)}
                  data-testid="template-card"
                >
                  <TemplateIcon>
                    <Icon name={config.icon} />
                  </TemplateIcon>
                  <TemplateInfoContainer>
                    <TemplateName>{t(config.nameKey)}</TemplateName>
                    <TemplateDescription>{t(config.descriptionKey)}</TemplateDescription>
                  </TemplateInfoContainer>
                </TemplateCard>
              );
            })}
          </TemplateGrid>
        </TemplateSection>

        {/* ── Reports table ── */}
        <Section>
          <SectionHeader>
            <SectionTitle>{t('new_reports_recent_reports')}</SectionTitle>
            <AdvancedFilter
              clearAllLabel={t('new_reports_filter_clear_all') as string}
              displayLabel="Filter"
              filters={[]}
              icon="plus_circle"
              onSelect={setSelectedFilters}
              selectedAllLabel={t('new_reports_filter_select_all') as string}
              selectedFilters={selectedFilters}
              unselectedAllLabel={t('new_reports_filter_unselect_all') as string}
              filterResultsText={t('new_reports_filter_results') as string}
            />
          </SectionHeader>
          {renderReports()}
        </Section>
      </ReportsListContent>

      {reportToDelete && (
        <DeleteReportModal
          reportName={reportToDelete.name}
          onDelete={handleDeleteReport}
          onCancel={() => setReportToDelete(null)}
        />
      )}
    </ReportsListContainer>
  );
};
